import type { Agent } from './types';
import { parseAgentRatingFromPayload } from '@/constants/agentModule';

export function parseWorkbenchBoolean(value: unknown): boolean {
  if (value === true || value === 1)
    return true;
  if (value === false || value === 0 || value == null)
    return false;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes')
    return true;
  if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === '')
    return false;
  return Boolean(value);
}

function mapAgentStatus(agentStatus: unknown): Agent['status'] {
  const s = String(agentStatus ?? '').toLowerCase();
  if (s === '0' || s === 'available' || s === 'online')
    return 'available';
  if (s === '1' || s === 'disabled')
    return 'disabled';
  return 'offline';
}

function mapTaskCapability(raw: unknown): string {
  const v = String(raw ?? '0');
  if (v === 'true')
    return '1';
  if (v === 'false')
    return '0';
  return v === '1' ? '1' : '0';
}

function resolveAgentNumericId(rawId: unknown): number {
  if (typeof rawId === 'string') {
    if (/^\d+$/.test(rawId))
      return Number(rawId);
    return 0;
  }
  return Number(rawId) || 0;
}

function parseNonNegativeInt(value: unknown): number {
  const num = Number(value ?? 0);
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

export function mapWorkbenchAgentItem(it: Record<string, unknown>): Agent {
  const idStr = String(it.idStr ?? it.id ?? '');
  return {
    id: idStr,
    rawId: idStr,
    numericId: resolveAgentNumericId(it.id ?? it.idStr ?? 0),
    name: String(it.agentNickname ?? '未命名智能体'),
    description: String(it.localizedDescription ?? ''),
    icon: String(it.agentIcon ?? 'User'),
    color: '#10A6E2',
    version: 'v1.0.0',
    // 展示用业务分类；平台渲染分支请用 modelCategory
    category: String(it.category ?? it.expertDomainName ?? ''),
    modelCategory: it.category != null ? String(it.category) : undefined,
    expertDomain: it.expertDomain != null ? String(it.expertDomain) : undefined,
    expertDomainName: it.expertDomainName != null ? String(it.expertDomainName) : undefined,
    taskCapability: mapTaskCapability(it.taskCapability),
    tagNames: Array.isArray(it.tagNames) ? it.tagNames.map(String) : undefined,
    status: mapAgentStatus(it.agentStatus),
    instantChatCount: parseNonNegativeInt(it.usageCount),
    taskExecutionCount: 0,
    likeCount: parseNonNegativeInt(it.likeCount),
    rating: parseAgentRatingFromPayload(it),
    ratingDisplay: it.ratingDisplay != null ? String(it.ratingDisplay) : undefined,
    isFavorite: parseWorkbenchBoolean(it.isFavorite),
    isLiked: parseWorkbenchBoolean(it.isLiked),
    hasEvaluated: parseWorkbenchBoolean(it.hasEvaluated),
    lastChatTime: it.lastChatTime != null ? String(it.lastChatTime) : undefined,
    createTime: it.createTime != null ? String(it.createTime) : undefined,
  };
}

export function buildWorkbenchListResponse(res: any) {
  const rows = Array.isArray(res?.rows) ? res.rows : [];
  const list = rows.map((it: Record<string, unknown>) => mapWorkbenchAgentItem(it));

  return {
    code: res?.code ?? 200,
    message: res?.msg ?? '操作成功',
    data: {
      list,
      total: Number(res?.total ?? list.length),
      page: 1,
      pageSize: list.length,
    },
    success: true,
    timestamp: Date.now(),
  };
}

export function normalizeAgentActionResponse(res: any, data?: unknown) {
  const code = res?.code ?? 200;
  return {
    code,
    msg: res?.msg ?? '操作成功',
    data: data ?? res?.data,
    success: Number(code) === 200,
  };
}

export function mapLikeToggleData(payload: Record<string, unknown>, fallbackLiked: boolean) {
  return {
    liked: parseWorkbenchBoolean(payload.liked ?? fallbackLiked),
    likeCount: parseNonNegativeInt(payload.likeCount),
  };
}

function logAgentApi(tag: string, payload: unknown) {
  if (import.meta.env.DEV)
    console.log(tag, payload);
}

export function logWorkbenchRequest(tag: string, payload: unknown) {
  logAgentApi(tag, payload);
}
